import { useSelector } from 'react-redux';
import { formatDateOrEmpty, toTimestamp } from '@helpers/dates';
import TableContainer from '@mui/material/TableContainer';
import { Table } from '@components/Table';
import Button from '@components/Button';
import Avatar from '@components/Avatar';
import LockedButton from '@features/LockedButton';
import { Typography } from '@components/Typography';
import { useTranslation, useTableSort } from '@hooks';
import { getFullName, getInitialsParts } from '@helpers/userName';
import { PRICING_DRAWER_TYPE } from '@constants/pricing-drawer-ui';
import usePermissions from '@hooks/usePermissions';
import { FORM_TYPE } from '../EditModal';

const SORT_COLUMNS = {
    name: user => getFullName(user.firstName, user.lastName),
    email: 'email',
    teams: user => (user.teams || []).map(team => team.name).join(', '),
    registrationDate: user => toTimestamp(user.registrationDate),
    lastVisit: user => toTimestamp(user.lastVisit),
};

const GetUsersColumnNames = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            sortKey: 'name',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.name')}
                </Typography>
            ),
        },
        {
            key: 1,
            sortKey: 'email',
            align: 'left',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.email')}
                </Typography>
            ),
        },
        {
            key: 2,
            sortKey: 'teams',
            align: 'left',
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.teams')}
                </Typography>
            ),
        },
        {
            key: 3,
            sortKey: 'registrationDate',
            align: 'left',
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsUsers.addedOn')}
                </Typography>
            ),
        },
        {
            key: 4,
            sortKey: 'lastVisit',
            align: 'left',
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('SettingsUsers.lastLogin')}
                </Typography>
            ),
        },
        { key: 5, component: <Typography align="left" variant="subtitle2"></Typography> },
    ];
};

const getRowsData = (users, account, currentUser, language, canManageUsers) => {
    return users.map(user => {
        const userIAM = user.id === currentUser.id;
        const userIsAccountOwner = user.id === account.ownerId;
        const hasAccess = canManageUsers && !userIAM && !userIsAccountOwner;

        return [
            {
                component: (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                            size="small"
                            fullName={`${user?.firstName} ${user?.lastName}`}
                            label={getInitialsParts(user?.firstName, user?.lastName)}
                            image={user?.profileImageUrl}
                        />
                        <Typography variant="subtitle2" sx={{ marginLeft: '8px' }}>
                            {`${user?.firstName} ${user?.lastName}`}
                        </Typography>
                    </div>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {user.email}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        sx={{
                            maxWidth: '360px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        variant="body2"
                        align="left"
                    >
                        {(user.teams || []).map(team => team.name).join(', ')}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" noWrap>
                        {formatDateOrEmpty(user.registrationDate, 'd MMM yyyy', language)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" noWrap>
                        {formatDateOrEmpty(user.lastVisit, 'd MMM yyyy', language)}
                    </Typography>
                ),
            },
            {
                value: !hasAccess ? null : user,
            },
        ];
    });
};

const UserTable = ({ currentUser, account, setSelectedUser, setDeleteModalOpen, setModal }) => {
    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const { hasSettingsAccess } = usePermissions();
    const { sort, onSortChange, sortedData } = useTableSort(account?.users || [], { columns: SORT_COLUMNS });

    const limitExceeded = account?.users?.length >= account?.usersMaxCount;
    const canManageUsers = hasSettingsAccess();

    return (
        <>
            <TableContainer>
                <Table
                    data={sortedData}
                    columnNames={GetUsersColumnNames()}
                    rows={getRowsData(sortedData, account, currentUser, language, canManageUsers)}
                    sort={sort}
                    onSortChange={onSortChange}
                    onPrimaryAction={({ value }) => {
                        setSelectedUser(value);
                        setModal(prev => ({ ...prev, open: true, type: FORM_TYPE.EDIT }));
                    }}
                    onSecondaryAction={({ value }) => {
                        setSelectedUser(value);
                        setDeleteModalOpen(true);
                    }}
                    isInteractive
                />
            </TableContainer>
            {canManageUsers && (
                <div style={{ padding: '24px 0' }}>
                    {limitExceeded || account?.billingDiffInSeconds < 0 ? (
                        <LockedButton tooltipType={1} variant="contained" drawerType={PRICING_DRAWER_TYPE.CHANGE}>
                            {t('buttons.addUser')}
                        </LockedButton>
                    ) : (
                        <Button
                            type="button"
                            variant="contained"
                            onClick={() => {
                                setSelectedUser(null);
                                setModal(prev => ({ ...prev, open: true, type: FORM_TYPE.NEW }));
                            }}
                        >
                            {t('buttons.addUser')}
                        </Button>
                    )}
                </div>
            )}
        </>
    );
};

export default UserTable;
